import { LayerFilterOption } from "fond/map/Legend/Adornments/LayerFilter";
import { ConfigurationUpsert, GroupConfig } from "fond/types";
import { FilterConfiguration, LayerStyle } from "fond/types/ProjectLayerConfig";

import { FamilialLayerConfig, groupConfigTemplate, layerConfigTemplate, makeBuckets, OverlayIds, sublayerTemplate } from "./configuration";

export enum CbgLayerId {
  UNEMPLOYMENT_PROPORTION = "census-block-groups-unemployed-proportion",
  MEDIAN_INCOME = "census-block-groups-median-income-dollars",
}
export const cbgLayerFilterOptions: LayerFilterOption[] = [
  { ID: CbgLayerId.UNEMPLOYMENT_PROPORTION, Label: "Unemployment Rate", Type: "Option" },
  { ID: CbgLayerId.MEDIAN_INCOME, Label: "Median Income ($)", Type: "Option" },
];

export const defaultCbgFilteredLayerIds = [CbgLayerId.MEDIAN_INCOME];

const key = "census-block-groups";

// The available data is as follows:
// "demographics_median_income_dollars": 78841.0,
// "demographics_unemployed_proportion": 0.07277289836888332,
// "demographics_device_household_count": 708.0,
// "demographics_household_average_size": 2.17,
// "demographics_over_60_household_count": 409.0,
// "demographics_under_18_household_count": 146.0,
// "demographics_bachelor_degree_proportion": 0.41880969875091845,
// "demographics_high_school_diploma_proportion": 0.9301983835415136,
// "demographics_internet_subscription_household_count": 691.0

/**
 * Create the CBG overlay configuration.
 */
export const cbgOverlayConfiguration = (): { configs: ConfigurationUpsert; groupConfig: GroupConfig } => {
  const { config: employmentLayerConfig, descendants: employmentDescendants } = createUnemploymentProportionLayer();
  const { config: medianIncomeLayerConfig, descendants: medianIncomeDescendants } = createMedianIncomeLayer();

  const layerConfigs = [employmentLayerConfig, medianIncomeLayerConfig];
  const descendants = [...employmentDescendants, ...medianIncomeDescendants];

  // Create the groups
  const cbg = groupConfigTemplate(OverlayIds.DEMOGRAPHICS, layerConfigs, "Demographics", true, OverlayIds.ROOT);

  // Assemble everything together
  return { configs: [...descendants, ...layerConfigs], groupConfig: cbg };
};

const createUnemploymentProportionLayer = (): FamilialLayerConfig => {
  const buckets = makeBuckets({
    layerId: CbgLayerId.UNEMPLOYMENT_PROPORTION,
    attribute: "demographics_unemployed_proportion",
    labels: ["0", "2.68", "5.21", "9.42"],
    splits: [0, 0.026807473598700244, 0.05212620027434842, 0.09416581371545547],
    colors: ["#4dd53e", "#c9e816", "#f5c802", "#f96a00", "#d53e4f"],
    includeLastBucket: true,
    includeNullBucket: true,
  });

  const descendants = buckets.flatMap((bucket) => {
    const styles = createEmploymentStyles(bucket.id, bucket.color, bucket.filter, 0.3);
    const sublayerConfig = sublayerTemplate({
      id: bucket.id,
      key,
      layerId: CbgLayerId.UNEMPLOYMENT_PROPORTION,
      label: bucket.label,
      filter: bucket.filter,
      styles,
    });
    return [...styles, sublayerConfig];
  });

  const config = layerConfigTemplate({
    id: CbgLayerId.UNEMPLOYMENT_PROPORTION,
    key,
    label: "Unemployment Rate (%)",
    styles: [],
    children: buckets.map(({ id }) => id),
    parentId: OverlayIds.DEMOGRAPHICS,
  });

  return { config, descendants };
};

const createMedianIncomeLayer = (): FamilialLayerConfig => {
  const buckets = makeBuckets({
    layerId: CbgLayerId.MEDIAN_INCOME,
    attribute: "demographics_median_income_dollars",
    // The splits are determined and slightly adjusted based on the Census Bureau's income in the United States.
    // Household income levels for each class level are as follows:
    // 1. Low class: Less than or equal to $30k
    // 2. Lower-middle class: $30k - $60k
    // 3. Middle income: $60k - $100k
    // 4. Upper-middle class: $100k - $150k
    // 5. Upper class: $150k+
    splits: [0, 30000, 60000, 100000, 150000],
    colors: ["#d73027", "#f46d43", "#fee08b", "#ffffbf", "#d9ef8b", "#a6d96a", "#66bd63", "#1a9850", "#4dd53e", "#2bf015"],
    includeLastBucket: true,
    // many census block groups have no income data
    includeNullBucket: true,
  });

  const descendants = buckets.flatMap((bucket) => {
    const styles = createEmploymentStyles(bucket.id, bucket.color, bucket.filter, 0.3);
    const sublayerConfig = sublayerTemplate({
      id: bucket.id,
      key,
      layerId: CbgLayerId.MEDIAN_INCOME,
      label: bucket.label,
      filter: bucket.filter,
      styles,
    });
    return [...styles, sublayerConfig];
  });

  const config = layerConfigTemplate({
    id: CbgLayerId.MEDIAN_INCOME,
    key,
    label: "Median Income ($)",
    styles: [],
    children: buckets.map(({ id }) => id),
    parentId: OverlayIds.DEMOGRAPHICS,
  });

  return { config, descendants };
};

const createEmploymentStyles = (layerId: string, color: string, filter?: FilterConfiguration | null, opacity?: number): LayerStyle[] => [
  {
    ID: `${layerId}-polygon-fill`,
    Name: `${layerId}-polygon-fill`,
    GlobalPosition: 1,
    ConfigurationID: layerId,
    ConfigurationType: "LAYER",
    Position: 0,
    MapboxStyle: {
      type: "fill",
      ...(filter?.Mapbox ? { filter: filter.Mapbox } : {}),
      paint: {
        "fill-opacity": ["case", ["boolean", ["feature-state", "isEditing"], false], 0, opacity ?? 0.5],
        "fill-color": ["case", ["boolean", ["feature-state", "isSelected"], false], "#FFFF00", color],
      },
    },
    RawStyles: {
      Type: "fill",
      FillOpacity: opacity ?? 0.5,
      FillColor: color,
    },
    Type: "STYLE",
  },
];
